import { useState, useMemo, useCallback } from 'react';

/**
 * useTableState - Hook universal de arquitectura para el control y procesamiento de Tableros.
 * Maneja centralizadamente: 
 * 1. Búsqueda y Filtros de Multi-selección con soporte Case-Insensitive.
 * 2. Ordenamiento alfabético, numérico y de fechas (Timestamp / ISO Strings).
 * 3. Paginación matemática con controles de límites.
 * 4. Visibilidad de columnas en tiempo real.
 * 5. Control de expiración de bloqueos temporales por concurrencia.
 * * @param {Array} data - Listado de datos hidratados listos para procesar.
 * @param {Object} initialSort - Configuración de ordenamiento por defecto { key, direction }.
 * @param {Object} initialColumns - Visibilidad de columnas por defecto { [key]: boolean }.
 * @param {Object} initialFilters - Filtros predeterminados (Ej: { estadoGestion: ['Pendiente'] }).
 * @param {number} recordsPerPage - Cantidad máxima de registros a mostrar por página.
 * @param {number} lockStaleTimeMs - Tiempo de expiración del bloqueo (Default: 30 minutos).
 */
export const useTableState = ({
    data = [],
    initialSort = { key: 'fechaCarga', direction: 'descending' },
    initialColumns = {},
    initialFilters = {},
    recordsPerPage = 100,
    lockStaleTimeMs = 1000 * 60 * 30 // 30 Minutos por defecto
}) => {
    // --- ESTADOS INTERNOS DE LA TABLA ---
    const [sortConfig, setSortConfig] = useState(initialSort);
    const [columnVisibility, setColumnVisibility] = useState(initialColumns);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [filterValues, setFilterValues] = useState(initialFilters);

    // --- MANEJADORES DE ESTADO (MEMOIZADOS PARA RENDIMIENTO) ---
    
    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending'
        }));
        setCurrentPage(1); // Volvemos a la página 1 ante cualquier re-ordenamiento
    }, []);

    const toggleColumn = useCallback((key) => {
        setColumnVisibility(prev => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const handleFilterChange = useCallback((vals) => {
        setFilterValues(vals || {});
        setCurrentPage(1); // Volvemos a la página 1 ante cualquier cambio de filtro
    }, []);

    const toggleRowExpansion = useCallback((rowId) => {
        setExpandedRowId(prev => (prev === rowId ? null : rowId));
    }, []);

    // --- AUXILIARES INTERNOS DE COMPARACIÓN ---

    /**
     * Convierte cualquier formato de fecha a milisegundos de forma segura para comparar.
     */
    const getMs = useCallback((val) => {
        if (!val) return 0;
        if (val && typeof val === 'object' && 'seconds' in val) {
            return val.seconds * 1000;
        }
        const d = new Date(val);
        return isNaN(d.getTime()) ? 0 : d.getTime();
    }, []);

    /**
     * Determina si un registro se encuentra bajo bloqueo activo por otro colaborador.
     */
    const checkIsLocked = useCallback((item, currentUserId) => {
        if (!item?.bloqueo) return false;
        const bloqueoTimestamp = item.bloqueo.timestamp?.toMillis 
            ? item.bloqueo.timestamp.toMillis() 
            : (item.bloqueo.timestamp?.seconds ? item.bloqueo.timestamp.seconds * 1000 : 0);
            
        const isStale = bloqueoTimestamp > 0 && (Date.now() - bloqueoTimestamp > lockStaleTimeMs);
        return item.bloqueo.userId !== currentUserId && !isStale;
    }, [lockStaleTimeMs]);

    /**
     * RESOLUTOR UNIFICADO COMERCIAL (Mapeador de Equivalencias)
     * Resuelve de forma inteligente inconsistencias de nombres de campos comerciales en Firestore.
     * Corregido: Ahora resuelve de forma síncrona el estado de cotización específico de cada bid en BloqueosArt.
     */
    const getRowValue = useCallback((item, key) => {
        if (!item) return '';
        if (key === 'canal') {
            return item.canal || item.tipoCanal || item.canalOrigen || '';
        }
        if (key === 'canalNombre' || key === 'canalNombreUnified') {
            return item.canalNombre || item.subcanal || item.canalNombreUnified || '';
        }
        if (key === 'subcanalNombre' || key === 'subcanalNombreUnified') {
            return item.subcanalNombre || item.subcanalAsociado || '';
        }
        if (key === 'vendedor') {
            return item.vendedor || item.vendedorAsociado || item.vendedorAsociadoNombre || '';
        }
        // ◄◄◄ ADAPTADOR SÍNCRONO PARA LA COMPAÑÍA COTIZADA ESPECÍFICA EN BLOQUEOS
        if (key === 'estadoCotizacion') {
            const found = item.cotizaciones?.find(c => c.compania === item.companiaBloqueo);
            return found?.estadoCotizacion || found?.estadoRow || found?.estado || item.estadoCotizacion || item.estadoRow || item.estado || 'Cotizada';
        }
        return item[key];
    }, []);

    // --- MOTOR DE PROCESAMIENTO MULTI-FILTRO (MEMOIZADO) ---
    const filteredData = useMemo(() => {
        return data.filter(item => {
            // 1. Buscador Global Inteligente
            const search = (filterValues.search || '').toLowerCase().trim();
            if (search) {
                // Buscamos coincidencia en cualquier propiedad string o número del objeto
                const matchInRow = Object.values(item).some(val => {
                    if (val === undefined || val === null) return false;
                    return String(val).toLowerCase().includes(search);
                });
                if (!matchInRow) return false;
            }

            // 2. Filtros Dinámicos (Multi-select)
            for (const [key, selectedValues] of Object.entries(filterValues)) {
                // ESCUDO DE SEGURIDAD 1: Saltamos buscador, fechas explícitas y rangos que terminan en Desde/Hasta
                if (
                    key === 'search' || 
                    key.startsWith('fecha') || 
                    key.endsWith('Desde') || 
                    key.endsWith('Hasta')
                ) {
                    continue; 
                }

                // ESCUDO DE SEGURIDAD 2 (Fail-Safe): Si no es un array, evitamos usar .some() para que no crasheé la app
                if (!Array.isArray(selectedValues)) {
                    continue;
                }

                if (selectedValues.length === 0) continue;

                // Accedemos al valor de forma segura mediante el mapeador comercial
                const itemValue = getRowValue(item, key);
                const normalizedItemVal = itemValue !== undefined && itemValue !== null 
                    ? String(itemValue).toUpperCase().trim() 
                    : '';

                const match = selectedValues.some(v => 
                    String(v).toUpperCase().trim() === normalizedItemVal
                );
                
                if (!match) return false;
            }

            // 3. Filtros de Rangos de Fechas Dinámicos
            // Detecta automáticamente cualquier par de claves 'fechaKeyDesde' y 'fechaKeyHasta'
            for (const [key, val] of Object.entries(filterValues)) {
                if (key.endsWith('Desde') && val) {
                    const fieldName = key.replace('Desde', '');
                    const dateValue = item[fieldName];
                    if (dateValue && dateValue < val) return false;
                }
                if (key.endsWith('Hasta') && val) {
                    const fieldName = key.replace('Hasta', '');
                    const dateValue = item[fieldName];
                    if (dateValue && dateValue > val) return false;
                }
            }

            return true;
        });
    }, [data, filterValues, getRowValue]);

    // --- MOTOR DE ORDENAMIENTO COMPUESTO ---
    const sortedData = useMemo(() => {
        const { key, direction } = sortConfig;
        if (!key) return filteredData;

        const dirMultiplier = direction === 'ascending' ? 1 : -1;

        return [...filteredData].sort((a, b) => {
            const valA = getRowValue(a, key);
            const valB = getRowValue(b, key);

            // A. Ordenamiento de importes/moneda o valores puramente numéricos
            if (typeof valA === 'number' && typeof valB === 'number') {
                return (valA - valB) * dirMultiplier;
            }

            // B. Ordenamiento de fechas (Timestamp / ISO Strings)
            const isDateA = valA && (valA.seconds || !isNaN(Date.parse(valA)));
            const isDateB = valB && (valB.seconds || !isNaN(Date.parse(valB)));
            if (isDateA && isDateB) {
                return (getMs(valA) - getMs(valB)) * dirMultiplier;
            }

            // C. Fallback: Ordenamiento alfabético estándar Case-Insensitive (localeCompare)
            const strA = valA !== undefined && valA !== null ? String(valA).toLowerCase() : '';
            const strB = valB !== undefined && valB !== null ? String(valB).toLowerCase() : '';

            return strA.localeCompare(strB, 'es', { numeric: true }) * dirMultiplier;
        });
    }, [filteredData, sortConfig, getMs, getRowValue]);

    // --- MOTOR DE PAGINACIÓN ---
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * recordsPerPage;
        return sortedData.slice(start, start + recordsPerPage);
    }, [sortedData, currentPage, recordsPerPage]);

    const totalPages = useMemo(() => {
        return Math.max(1, Math.ceil(filteredData.length / recordsPerPage));
    }, [filteredData.length, recordsPerPage]);

    return {
        // Estados
        sortConfig,
        columnVisibility,
        currentPage,
        selectedRowId,
        expandedRowId,
        filterValues,
        recordsPerPage,
        
        // Datos Procesados
        filteredData,
        sortedData,
        paginatedData,
        totalPages,

        // Setters Manuales
        setSortConfig,
        setColumnVisibility,
        setCurrentPage,
        setSelectedRowId,
        setExpandedRowId,
        setFilterValues,

        // Handlers de Acción
        handleSort,
        toggleColumn,
        handleFilterChange,
        toggleRowExpansion,
        checkIsLocked,
        getMs
    };
};

export default useTableState;